import { checkRateLimit, rateLimitResponse } from '~/lib/security.server'
import { STATIC_PAGE_HEADERS } from "~/lib/cache.server"
import type { MetaFunction, ActionFunctionArgs } from 'react-router'
import { Form, useActionData, Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { sendEmail, emailTemplate } from '@afficheservices/email'
import { Mail, MapPin, Clock, CheckCircle, ArrowRight, ShieldCheck, Lock } from 'lucide-react'
import { StructuredData } from '~/components/structured-data'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Contact — AfficheServices' },
  { name: 'description', content: 'Contactez l\'équipe AfficheServices. Nous sommes là pour répondre à toutes vos questions.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/contact' },
  { property: 'og:title', content: 'Contact — AfficheServices' },
  { property: 'og:description', content: 'Contactez l\'équipe AfficheServices. Nous sommes là pour répondre à toutes vos questions.' },
  { property: 'og:url', content: 'https://www.afficheservices.fr/contact' },
  { property: 'og:type', content: 'website' },
  { property: 'og:image', content: 'https://www.afficheservices.fr/api/og?title=Contact&subtitle=Reponse+sous+24h' },
]

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

export async function action({ request }: ActionFunctionArgs) {
  if (!checkRateLimit(request, 'form')) return rateLimitResponse()
  const formData = await request.formData()
  const name = (formData.get('name') as string)?.trim()
  const email = (formData.get('email') as string)?.trim()
  const subject = (formData.get('subject') as string)?.trim()
  const message = (formData.get('message') as string)?.replace(/<[^>]*>/g, '').replace(/javascript:/gi, '').trim()

  if (!name || !email || !subject || !message) {
    const fieldErrors: { name?: boolean; email?: boolean; subject?: boolean; message?: boolean } = {}
    if (!name) fieldErrors.name = true
    if (!email) fieldErrors.email = true
    if (!subject) fieldErrors.subject = true
    if (!message) fieldErrors.message = true
    return { error: 'Tous les champs sont requis', fieldErrors }
  }
  if (message.length < 10) {
    return { error: 'Le message doit contenir au moins 10 caractères' }
  }

  // Honeypot check (spam protection)
  const honeypot = formData.get('website') as string
  if (honeypot) {
    // Bot detected — silently accept
    return { success: true }
  }

  // Skip email if RESEND_API_KEY not configured
  if (!process.env.RESEND_API_KEY) {
    // Email silently skipped — RESEND_API_KEY not configured
    return { success: true }
  }

  try {
    await sendEmail({
      to: 'contact@afficheservices.fr',
      subject: `[Contact] ${subject} — ${name}`,
      html: emailTemplate({
        title: 'Nouveau message de contact',
        body: `
          <table style="width: 100%; border-collapse: collapse;">
            <tr><td style="padding: 8px 0; color: #64748b; width: 100px;">Nom</td><td style="padding: 8px 0; font-weight: 600;">${escapeHtml(name)}</td></tr>
            <tr><td style="padding: 8px 0; color: #64748b;">Email</td><td style="padding: 8px 0;"><a href="mailto:${escapeHtml(email)}" style="color: #2563eb;">${escapeHtml(email)}</a></td></tr>
            <tr><td style="padding: 8px 0; color: #64748b;">Sujet</td><td style="padding: 8px 0;">${escapeHtml(subject)}</td></tr>
          </table>
          <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 16px 0;" />
          <div style="white-space: pre-wrap; color: #334155;">${escapeHtml(message)}</div>
        `,
      }),
    })
  } catch (err) {
    console.error('Contact email failed:', err)
    return { error: 'Erreur lors de l\'envoi. Veuillez réessayer.' }
  }

  return { success: true }
}

const CONTACT_INFO = [
  {
    icon: Mail,
    label: 'Email',
    value: 'contact@afficheservices.fr',
    href: 'mailto:contact@afficheservices.fr',
  },
  {
    icon: MapPin,
    label: 'Adresse',
    value: 'Paris, France',
    href: undefined,
  },
  {
    icon: Clock,
    label: 'Horaires',
    value: 'Lun-Ven 9h-18h',
    href: undefined,
  },
]


export default function Contact() {
  const actionData = useActionData<typeof action>()

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <StructuredData data={{
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'Contact AfficheServices',
        url: 'https://www.afficheservices.fr/contact',
        description: "Contactez l'équipe AfficheServices : support, partenariats, presse. Réponse sous 24h ouvrées.",
        mainEntity: {
          '@type': 'LocalBusiness',
          '@id': 'https://www.afficheservices.fr/#organization',
          name: 'AfficheServices',
          email: 'contact@afficheservices.fr',
          url: 'https://www.afficheservices.fr',
          areaServed: { '@type': 'Country', name: 'France' },
          contactPoint: [{
            '@type': 'ContactPoint',
            email: 'contact@afficheservices.fr',
            contactType: 'customer service',
            availableLanguage: 'French',
            areaServed: 'FR',
          }],
          openingHoursSpecification: [{
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
            opens: '08:00',
            closes: '20:00',
          }],
        },
      }} />

      <div className="mx-auto max-w-3xl px-4 py-16 lg:px-6">
        <h1 className="text-3xl font-bold text-slate-900">Contactez-nous</h1>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          Une question, une suggestion ou besoin d'aide ? Notre équipe est à votre disposition pour
          vous accompagner.
        </p>

        {/* Response time indicator */}
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-xs font-medium text-emerald-700">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
          Temps de réponse moyen : moins de 24h
        </div>

        {/* Direct channels — email only (phone temporarily disabled). */}
        <div className="mt-6 grid gap-3">
          <a
            href="mailto:contact@afficheservices.fr"
            className="group flex items-center gap-3 rounded-xl border border-border bg-white p-4 shadow-xs transition-all hover:border-primary hover:shadow-md"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Mail className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-xs text-slate-500">Écrivez-nous · réponse sous 24h</p>
              <p className="truncate text-sm font-semibold text-slate-900 group-hover:text-primary">contact@afficheservices.fr</p>
            </div>
          </a>
        </div>

        <div className="mt-10 grid gap-10 lg:grid-cols-3">
          {/* Form */}
          <div className="lg:col-span-2">
            {actionData?.success ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
                <CheckCircle className="mx-auto h-10 w-10 text-emerald-600" />
                <h3 className="mt-3 text-lg font-semibold text-slate-900">Message envoyé !</h3>
                <p className="mt-2 text-sm text-slate-600">Nous avons bien reçu votre message.</p>
                <p className="mt-1 text-xs text-slate-500">Vous recevrez une réponse par email sous 24 heures ouvrées (lun-ven, heure de Paris).</p>
                <Link to="/faq" className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
                  Consulter la FAQ en attendant <ArrowRight className="h-3 w-3" aria-hidden="true" />
                </Link>
              </div>
            ) : (
            <Form method="post" className="space-y-5">
              {actionData?.error && (
                <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{actionData.error}</div>
              )}
              {/* Honeypot — hidden from humans, visible to bots */}
              <input type="text" name="website" className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700">
                  Nom complet
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  autoComplete="name"
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Votre nom"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                  Adresse email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="votre@email.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-slate-700">
                  Sujet
                </label>
                <select
                  id="subject"
                  name="subject"
                  required
                  autoComplete="off"
                  defaultValue="devis"
                  aria-invalid={actionData?.fieldErrors?.subject ? true : undefined}
                  aria-describedby={actionData?.fieldErrors?.subject ? 'subject-error' : undefined}
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="devis">À propos d'un devis</option>
                  <option value="general">Question générale</option>
                  <option value="artisan">Problème avec un artisan</option>
                  <option value="pro">Inscription professionnelle</option>
                  <option value="technique">Problème technique</option>
                  <option value="autre">Autre</option>
                </select>
                {actionData?.fieldErrors?.subject && (
                  <span id="subject-error" role="alert" className="mt-1.5 block text-sm text-red-600">
                    Sélectionnez le sujet de votre demande.
                  </span>
                )}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-700">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  autoComplete="off"
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Décrivez votre demande..."
                />
              </div>

              <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
                Vos données restent privées · Pas de spam · Conforme RGPD
              </p>

              <button
                type="submit"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover btn-press"
              >
                Envoyer le message <ArrowRight className="h-4 w-4" />
              </button>

              <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500">
                <Lock className="h-3 w-3" aria-hidden="true" />
                Vos données sont chiffrées et ne seront jamais revendues
              </p>
            </Form>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {CONTACT_INFO.map((item) => (
              <div key={item.label} className="rounded-xl border border-slate-100 p-4">
                <div className="flex items-start gap-3">
                  <item.icon className="mt-0.5 h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium text-slate-900">{item.label}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="mt-0.5 text-sm text-slate-600 hover:text-primary"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="mt-0.5 text-sm text-slate-600">{item.value}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Contact FAQ */}
      <section className="border-t border-slate-100 bg-surface-secondary py-14">
        <div className="mx-auto max-w-3xl px-4 lg:px-6">
          <h2 className="text-center text-xl font-bold text-slate-900">Questions fréquentes</h2>
          <div className="mt-6 space-y-3">
            {[
              { q: 'Quel est le délai de réponse du service client ?', a: 'Notre équipe répond sous 24 heures ouvrées par email. Pour les urgences, utilisez le formulaire de devis — un artisan disponible 24/7 vous rappelle sous 30 minutes.' },
              { q: 'Comment signaler un problème avec un artisan ?', a: 'Envoyez-nous un email à contact@afficheservices.fr avec les détails (numéro de devis, photos, description). Nous enquêtons sous 48h et pouvons suspendre le compte du professionnel si nécessaire.' },
              { q: 'Puis-je modifier ma demande de devis après envoi ?', a: 'Oui, contactez-nous avec votre numéro de demande. Nous pouvons modifier la description, ajouter des photos ou annuler la demande tant qu\'aucun artisan n\'a encore répondu.' },
              { q: 'Le service est-il disponible dans toute la France ?', a: 'Oui, AfficheServices couvre toute la France métropolitaine. Notre réseau d\'artisans est particulièrement dense dans les 20 plus grandes agglomérations et s\'étend progressivement aux zones rurales.' },
            ].map((faq, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-slate-900">{faq.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
